import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogSummaries as blogPosts } from '../../data/blogSummaries';
import { AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ShoppingBagIcon,
  InformationCircleIcon,
  PhoneIcon,
  BookOpenIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

const Navbar: React.FC = React.memo(() => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<string | null>(null);

  const navItems = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'Store', path: '/store', icon: ShoppingBagIcon },
    { name: 'Editor', path: '/editor', icon: PencilSquareIcon },
    { name: 'Blog', path: '/blog', icon: BookOpenIcon },
    { name: 'FAQ', path: '/faq', icon: QuestionMarkCircleIcon },
    { name: 'About', path: '/about', icon: InformationCircleIcon },
    { name: 'Contact', path: '/contact', icon: PhoneIcon },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 backdrop-blur-xl border-b border-gray-700/50 sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 group">
              <motion.div
                whileHover={{ scale: 1.08 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="relative w-12 h-12 flex items-center justify-center"
              >
                {/* Animated gradient ring */}
                <motion.div
                  className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 opacity-80"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                />
                <div className="absolute inset-[2px] bg-gray-900 rounded-2xl" />

                {/* Logo icon */}
                <svg className="w-7 h-7 relative z-10" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Stylized 'T' and 'M' monogram */}
                  {/* T */}
                  <path d="M6 8 L14 8 L14 11 L11.5 11 L11.5 24 L8.5 24 L8.5 11 L6 11 Z" fill="url(#logoGrad1)" />
                  {/* M */}
                  <path d="M16 8 L19 8 L21 14 L23 8 L26 8 L26 24 L23.5 24 L23.5 12 L21.5 18 L20.5 18 L18.5 12 L18.5 24 L16 24 Z" fill="url(#logoGrad2)" />
                  <defs>
                    <linearGradient id="logoGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#A78BFA" />
                      <stop offset="100%" stopColor="#818CF8" />
                    </linearGradient>
                    <linearGradient id="logoGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06B6D4" />
                      <stop offset="100%" stopColor="#0EA5E9" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
              <div className="hidden sm:block">
                <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Thumbnail Makers
                </div>
                <div className="text-xs text-gray-400 -mt-1">Free Assets</div>
              </div>
            </Link>
          </div>

          {/* Center: Nav Links */}
          <div className="hidden md:flex flex-1 justify-center max-w-2xl mx-8">
            <div className="flex items-center gap-1 bg-gray-800/50 rounded-full p-1 border border-gray-700/50">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const isBlog = item.name === 'Blog';

                if (isBlog) {
                  return (
                    <div
                      key={item.path}
                      className="relative"
                      onMouseEnter={() => setActiveDropdown('blog')}
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <Link
                        to={item.path}
                        className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${active
                          ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white shadow-lg'
                          : 'text-gray-300 hover:text-white hover:bg-gray-700/80'
                          }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="hidden lg:inline">{item.name}</span>
                        <ChevronDownIcon className="h-3 w-3 hidden lg:block" />
                      </Link>

                      <AnimatePresence>
                        {activeDropdown === 'blog' && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-gray-900/95 backdrop-blur-xl border border-gray-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[60vh] overflow-y-auto custom-scrollbar"
                          >
                            <div className="p-2 space-y-1">
                              {blogPosts.map((post) => (
                                <Link
                                  key={post.id}
                                  to={`/blog/${post.id}`}
                                  className="block p-3 rounded-xl hover:bg-gray-800/50 transition-colors group"
                                >
                                  <div className="text-sm font-medium text-gray-200 group-hover:text-white line-clamp-2 leading-snug">
                                    {post.title}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                    <span className="bg-gray-800 px-1.5 py-0.5 rounded text-[10px]">{post.category}</span>
                                    <span>• {post.readTime} min read</span>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.name}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${active
                      ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700/80'
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden lg:inline">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right: Empty space (removed auth buttons) */}
          <div className="w-32" />
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-gray-700/50 mt-4 pt-4">
          <div className="flex items-center justify-center gap-1 overflow-x-auto pb-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const isBlog = item.name === 'Blog';

              if (isBlog) {
                return (
                  <div key={item.path} className="flex flex-col items-center relative group">
                    <button
                      onClick={() => setMobileMenuOpen(mobileMenuOpen === 'blog' ? null : 'blog')}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200 min-w-0 ${active
                        ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                        }`}
                    >
                      <div className="relative">
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full  animate-pulse" />
                      </div>
                      <span className="text-xs font-medium truncate">{item.name}</span>
                    </button>

                    <AnimatePresence>
                      {mobileMenuOpen === 'blog' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 bg-gray-900/95 backdrop-blur-xl border border-gray-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[50vh] overflow-y-auto custom-scrollbar"
                        >
                          <div className="p-2 space-y-1">
                            <Link
                              to="/blog"
                              onClick={() => setMobileMenuOpen(null)}
                              className="block p-3 rounded-xl bg-gray-800/50 hover:bg-gray-800 transition-colors text-center font-bold text-white mb-2"
                            >
                              View All Posts
                            </Link>
                            {blogPosts.map((post) => (
                              <Link
                                key={post.id}
                                to={`/blog/${post.id}`}
                                onClick={() => setMobileMenuOpen(null)}
                                className="block p-2 rounded-lg hover:bg-gray-800/50 transition-colors"
                              >
                                <div className="text-xs font-medium text-gray-300 line-clamp-2">
                                  {post.title}
                                </div>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200 min-w-0 ${active
                    ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    }`}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-xs font-medium truncate">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
});

export default Navbar;
